import React from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Tooltip,
} from "@mui/material";
import { markAllAsRead } from "api/NotifyAPI";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MarkUnreadIcon from "@mui/icons-material/DoneAll"; // Icon "Đọc tất cả"
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Định nghĩa component với function declaration
function NotificationDrawer({
  openNotificationDrawer,
  setOpenNotificationDrawer,
  notifications,
  iconMap,
  onRefreshDataChange,
}) {
  const navigate = useNavigate();
  const handleMarkAllAsRead = async () => {
    const result = await Swal.fire({
      title: "Đánh dấu tất cả là đã đọc?",
      text: "Hành động này sẽ đánh dấu toàn bộ thông báo là đã đọc.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Xác nhận",
      cancelButtonText: "Hủy",
      customClass: {
        confirmButton: "swal2-confirm-custom",
        cancelButton: "swal2-cancel-custom",
      },
    });

    if (result.isConfirmed) {
      await markAllAsRead();
      onRefreshDataChange();

      Swal.fire({
        title: "Thành công!",
        text: "Tất cả thông báo đã được đánh dấu là đã đọc.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };
  return (
    <Drawer
      anchor="right"
      open={openNotificationDrawer}
      onClose={() => setOpenNotificationDrawer(false)}
      ModalProps={{ BackdropProps: { invisible: true } }}
      PaperProps={{
        sx: {
          width: 360,
          boxShadow: 3,
          right: 0,
          borderTopLeftRadius: 12, // Bo góc mềm mại hơn
          borderBottomLeftRadius: 12,
          background: "linear-gradient(to top, #2C5743, #1F3A2D)", // Gradient cho nền
        },
        style: {
          color: "white !important", // Màu chữ trắng
        },
      }}
    >
      <Box p={2}>
        {/* Thêm nút đóng X */}
        <IconButton
          onClick={() => setOpenNotificationDrawer(false)}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "white !important", // Màu của nút đóng
            "&:hover": {
              backgroundColor: "#1C3A31", // Thêm hiệu ứng hover cho nút đóng
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontSize: "20px",
            color: "white !important",
            fontWeight: "600",
            display: "flex",
            alignItems: "center", // Để căn chỉnh các item theo chiều ngang
          }}
        >
          Thông báo
          {/* Thêm nút "Đọc tất cả" */}
          <Tooltip title="Đánh dấu tất cả là đã đọc">
            <IconButton
              onClick={() => handleMarkAllAsRead()}
              sx={{
                color: "white !important",
                ml: 2, // Thêm một khoảng cách cho nút
              }}
            >
              <MarkUnreadIcon />
            </IconButton>
          </Tooltip>
        </Typography>
        <Box
          sx={{
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            pr: 1,
            scrollBehavior: "smooth",
          }}
        >
          <List>
            {notifications?.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  padding: "12px 16px", // Khoảng cách giữa các item
                  color: "white !important", // Màu chữ thông báo
                  alignItems: "center",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)", // Thêm shadow đậm hơn
                  borderRadius: "8px", // Bo góc
                  marginBottom: "12px", // Khoảng cách giữa các mục
                  backgroundColor: item.isRead === 0 ? "#4F856E" : "#3D6F59", // Nếu chưa đọc, dùng màu khác
                  "&:hover": {
                    backgroundColor: item.isRead === 0 ? "#5A9E82" : "#4F856E", // Hiệu ứng hover cho thông báo chưa đọc
                    cursor: "pointer", // Thay đổi con trỏ thành bàn tay
                    transform: "translateY(-2px)", // Thêm hiệu ứng nâng lên khi hover
                    transition: "all 0.3s ease", // Hiệu ứng chuyển động mượt mà
                  },
                }}
                onClick={() => {
                  setOpenNotificationDrawer(false);
                  navigate(item.linkMenu, { state: { itemData: item } });
                }}
              >
                <ListItemIcon sx={{ color: "white !important", mt: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: "transparent",
                      border: "2px solid #ffffff", // Viền mỏng trắng cho avatar
                    }}
                  >
                    {iconMap[item.type] || <NotificationsIcon sx={{ color: "white" }} />}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={item.message}
                  secondary="1 phút trước" // Hiển thị thời gian
                  secondaryTypographyProps={{
                    sx: {
                      fontSize: "12px",
                      color: "#E6E6E6 !important", // Màu sắc cho thời gian (sáng hơn)
                      marginTop: "5px",
                    },
                  }}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: "15px",
                      color: item.isRead === 0 ? "#FF9800" : "white !important", // Màu vàng cho thông báo chưa đọc
                      fontWeight: item.isRead === 0 ? "bold" : "500", // In đậm cho thông báo chưa đọc
                      wordBreak: "break-word", // Giúp tự xuống dòng nếu quá dài
                      whiteSpace: "pre-line", // Cho phép xuống dòng khi cần
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Drawer>
  );
}

// Thêm prop types để kiểm tra kiểu dữ liệu của props
NotificationDrawer.propTypes = {
  openNotificationDrawer: PropTypes.bool.isRequired, // Kiểu dữ liệu boolean cho trạng thái mở
  setOpenNotificationDrawer: PropTypes.func.isRequired, // Kiểu dữ liệu function cho setter
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired, // Kiểu dữ liệu cho id
      message: PropTypes.string.isRequired, // Kiểu dữ liệu cho message
      timestamp: PropTypes.number.isRequired, // Kiểu dữ liệu cho timestamp
      type: PropTypes.string.isRequired, // Kiểu dữ liệu cho type
    })
  ).isRequired,
  iconMap: PropTypes.objectOf(PropTypes.element).isRequired, // Kiểu dữ liệu object của các React elements
  onRefreshDataChange: PropTypes.func.isRequired, // Hàm đánh dấu tất cả là đã đọc
};

export default NotificationDrawer;
